/**
 * TaxonomyFilter Component
 * 
 * Hierarchical product filter driven by taxonomy.ts configuration.
 * Replaces the 5 hardcoded Collapsible* filter components.
 * 
 * Supports drilling down: Collection → ProductType → Category → Tag
 * with multi-select at tag level and cascading filter behavior.
 */

import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { 
  TaxonomyNode, 
  ROOT_TAXONOMY,
  TAXONOMY_BY_COLLECTION,
  HIERARCHICAL_COLLECTIONS,
  CLOTHING_FILTER_NODES
} from '@/config/taxonomy';

export interface TaxonomyFilterState {
  /** Selected collection handle (e.g., 'apparel') */
  collection?: string;
  /** Selected productType handle (e.g., 'tops') */
  productType?: string;
  /** Selected category handle (e.g., 'blouses') */
  category?: string;
  /** Selected tag handles (e.g., ['button-up-blouses', 'long-sleeve-blouses']) */
  tags: string[];
}

/** Product counts for each filter option */
export interface TaxonomyFilterCounts {
  /** Map of handle -> count for all filter options */
  [handle: string]: number;
}

export interface TaxonomyFilterProps {
  /** Current filter state */
  value: TaxonomyFilterState;
  /** Callback when filter changes */
  onChange: (state: TaxonomyFilterState) => void;
  /** Callback to reset page to 1 */
  onPageReset?: () => void;
  /** Available collections to show (if undefined, shows all) */
  availableCollections?: string[];
  /** Show only specific collection's hierarchy (for collection-specific pages) */
  rootCollection?: string;
  /** Product counts for each filter option */
  counts?: TaxonomyFilterCounts;
  /** Total product count (for "All Products" option) */
  totalCount?: number;
  /** Custom label for "All Products" option (e.g., "All Apparel") */
  allLabel?: string;
  /** Custom class name */
  className?: string;
}

/**
 * Recursive component to render a taxonomy node and its children
 */
const TaxonomyNodeItem: React.FC<{
  node: TaxonomyNode;
  depth: number;
  filterState: TaxonomyFilterState;
  onSelect: (node: TaxonomyNode) => void;
  onTagToggle: (tagHandle: string, checked: boolean) => void;
  expandedNodes: Set<string>;
  onToggleExpand: (nodeId: string) => void;
  parentLabel?: string; // For truncating repetitive prefixes
  counts?: TaxonomyFilterCounts;
}> = ({ node, depth, filterState, onSelect, onTagToggle, expandedNodes, onToggleExpand, parentLabel, counts }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const count = counts?.[node.handle];
  
  // Determine if this node is selected based on its type
  const isSelected = useMemo(() => {
    switch (node.type) {
      case 'collection':
        return filterState.collection === node.handle;
      case 'productType':
        return filterState.productType === node.handle;
      case 'category':
        return filterState.category === node.handle;
      case 'tag':
      case 'vendor':
        return filterState.tags.includes(node.handle);
      default:
        return false;
    }
  }, [node, filterState]);

  // Use displayLabel from taxonomy config if available, otherwise fall back to truncation logic
  const displayLabelText = useMemo(() => {
    // First check if the node has an explicit displayLabel in the taxonomy config
    if (node.displayLabel) {
      return node.displayLabel;
    }
    // Fall back to truncating parent prefix if applicable
    if (parentLabel && node.label.startsWith(parentLabel)) {
      const truncated = node.label.slice(parentLabel.length).replace(/^[\s&-]+/, '');
      return truncated || node.label;
    }
    return node.label;
  }, [node.label, node.displayLabel, parentLabel]);

  // For tags and vendors, use checkbox. Handle tags with children (e.g., Wide Leg Pants with sub-options)
  if (node.type === 'tag' || node.type === 'vendor') {
    // Check if this is a nested tag (has a parent with children that share prefix)
    const isNestedTag = parentLabel && node.label.startsWith(parentLabel);
    const hasChildren = node.children && node.children.length > 0;
    
    // If tag has children, render as expandable section
    if (hasChildren) {
      return (
        <div>
          <Collapsible open={isExpanded} onOpenChange={() => onToggleExpand(node.id)}>
            <div 
              className="flex items-center gap-1 py-1 cursor-pointer hover:text-foreground transition-colors group"
              style={{ paddingLeft: `${depth * 12}px` }}
            >
              {/* Checkbox for this tag */}
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => onTagToggle(node.handle, !!checked)}
                className="h-3.5 w-3.5"
              />
              
              {/* Label */}
              <span 
                className={cn(
                  "text-xs flex-1",
                  isSelected ? "text-foreground font-medium" : "text-muted-foreground"
                )}
              >
                {displayLabelText}
                {count !== undefined && count > 0 && (
                  <span className="text-muted-foreground/70 font-normal ml-1">({count})</span>
                )}
              </span>
              
              {/* Expand/collapse chevron */}
              <CollapsibleTrigger asChild>
                <button className="p-0.5 hover:bg-muted/50 rounded transition-colors">
                  {isExpanded ? (
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                </button>
              </CollapsibleTrigger>
            </div>
            
            {/* Children - indented one more level */}
            <CollapsibleContent>
              <div className="space-y-0.5">
                {node.children!.map(child => (
                  <TaxonomyNodeItem
                    key={child.id}
                    node={child}
                    depth={depth + 1}
                    filterState={filterState}
                    onSelect={onSelect}
                    onTagToggle={onTagToggle}
                    expandedNodes={expandedNodes}
                    onToggleExpand={onToggleExpand}
                    parentLabel={node.label}
                    counts={counts}
                  />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      );
    }
    
    // Simple tag without children
    return (
      <label 
        className="flex items-center gap-2 py-1 cursor-pointer hover:text-foreground transition-colors"
        style={{ paddingLeft: `${(depth + (isNestedTag ? 1 : 0)) * 12}px` }}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onTagToggle(node.handle, !!checked)}
          className="h-3.5 w-3.5"
        />
        <span className={cn(
          "text-xs flex-1",
          isSelected ? "text-foreground font-medium" : "text-muted-foreground",
          isNestedTag && "italic" // Visual distinction for nested items
        )}>
          {displayLabelText}
        </span>
        {count !== undefined && count > 0 && (
          <span className="text-xs text-muted-foreground/70">({count})</span>
        )}
      </label>
    );
  }

  // For collection/productType/category - expandable nodes
  return (
    <div>
      <Collapsible open={isExpanded} onOpenChange={() => onToggleExpand(node.id)}>
        <div 
          className="flex items-center gap-1 py-1.5 cursor-pointer hover:text-foreground transition-colors group"
          style={{ paddingLeft: `${depth * 12}px` }}
        >
          {/* Radio button for selection */}
          <input
            type="radio"
            name={`taxonomy-${node.type}`}
            checked={isSelected}
            onChange={() => onSelect(node)}
            className="w-3.5 h-3.5 accent-primary cursor-pointer"
          />
          
          {/* Label */}
          <span 
            className={cn(
              "text-sm flex-1",
              isSelected ? "text-foreground font-medium" : "text-muted-foreground"
            )}
            onClick={() => onSelect(node)}
          >
            {node.label}
            {count !== undefined && count > 0 && (
              <span className="text-muted-foreground/70 font-normal ml-1">({count})</span>
            )}
          </span>
          
          {/* Expand/collapse chevron */}
          {hasChildren && (
            <CollapsibleTrigger asChild>
              <button className="p-0.5 hover:bg-muted/50 rounded transition-colors">
                {isExpanded ? (
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                )}
              </button>
            </CollapsibleTrigger>
          )}
        </div>
        
        {/* Children */}
        {hasChildren && (
          <CollapsibleContent>
            <div className="space-y-0.5">
              {node.children!.map(child => (
                <TaxonomyNodeItem
                  key={child.id}
                  node={child}
                  depth={depth + 1}
                  filterState={filterState}
                  onSelect={onSelect}
                  onTagToggle={onTagToggle}
                  expandedNodes={expandedNodes}
                  onToggleExpand={onToggleExpand}
                  parentLabel={node.type === 'category' && child.type === 'tag' ? node.label : undefined}
                  counts={counts}
                />
              ))}
            </div>
          </CollapsibleContent>
        )}
      </Collapsible>
    </div>
  );
};

// Helper to compute which nodes should be expanded based on filter state
function computeExpandedNodes(value: TaxonomyFilterState): Set<string> {
  const initial = new Set<string>();
  
  // Search all taxonomy trees (both CLOTHING_FILTER_NODES and ROOT_TAXONOMY)
  const allTrees = [...CLOTHING_FILTER_NODES, ...ROOT_TAXONOMY];
  
  // Recursive helper to find and expand path to a node by handle
  const findAndExpandPath = (nodes: TaxonomyNode[], targetHandle: string, targetType: string, path: string[] = []): boolean => {
    for (const node of nodes) {
      if (node.handle === targetHandle && node.type === targetType) {
        // Found it - add all nodes in path plus this node
        path.forEach(id => initial.add(id));
        initial.add(node.id);
        return true;
      }
      if (node.children) {
        if (findAndExpandPath(node.children, targetHandle, targetType, [...path, node.id])) {
          return true;
        }
      }
    }
    return false;
  };
  
  // Expand path to selected collection
  if (value.collection) {
    findAndExpandPath(allTrees, value.collection, 'collection', []);
  }
  
  // Expand path to selected productType
  if (value.productType) {
    findAndExpandPath(allTrees, value.productType, 'productType', []);
  }
  
  // Expand path to selected category
  if (value.category) {
    findAndExpandPath(allTrees, value.category, 'category', []);
  }
  
  // Expand path to selected tags
  for (const tagHandle of value.tags) {
    findAndExpandPath(allTrees, tagHandle, 'tag', []);
  }
  
  return initial;
}

export const TaxonomyFilter: React.FC<TaxonomyFilterProps> = ({
  value,
  onChange,
  onPageReset,
  availableCollections,
  rootCollection,
  counts,
  totalCount,
  allLabel = 'All Products',
  className,
}) => {
  // Track which nodes are expanded - compute initial state from URL values
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => {
    return computeExpandedNodes(value);
  });
  
  // Re-compute expanded nodes when URL-driven filter state changes on navigation
  React.useEffect(() => {
    setExpandedNodes(prev => {
      const needed = computeExpandedNodes(value);
      // Only update if we need to expand more nodes than currently expanded
      // This prevents collapsing manually expanded nodes
      const hasNewExpansions = Array.from(needed).some(id => !prev.has(id));
      if (hasNewExpansions) {
        return new Set([...prev, ...needed]);
      }
      return prev;
    });
  }, [value.productType, value.category, value.tags]);

  // Get root nodes to display
  const rootNodes = useMemo(() => {
    // For apparel page, show combined clothing filter (Tops, Bottoms, Outfit Sets, Dresses, One-Pieces)
    if (rootCollection === 'apparel') {
      return CLOTHING_FILTER_NODES;
    }
    
    if (rootCollection) {
      const collectionNode = TAXONOMY_BY_COLLECTION[rootCollection];
      return collectionNode?.children || [];
    }
    
    if (availableCollections) {
      return ROOT_TAXONOMY.filter(node => availableCollections.includes(node.handle));
    }
    
    return ROOT_TAXONOMY;
  }, [rootCollection, availableCollections]);

  // Helper to find label by handle from taxonomy tree
  const findLabelByHandle = (handle: string): string => {
    // Search recursively through all nodes
    const searchNodes = (nodes: TaxonomyNode[]): string | undefined => {
      for (const node of nodes) {
        if (node.handle === handle) return node.label;
        if (node.children) {
          const found = searchNodes(node.children);
          if (found) return found;
        }
      }
      return undefined;
    };
    
    // Search in CLOTHING_FILTER_NODES first (covers most cases)
    const result = searchNodes(CLOTHING_FILTER_NODES);
    if (result) return result;
    
    // Fallback: convert handle to title case
    return handle.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Get active filter chips data
  const activeChips = useMemo(() => {
    const chips: Array<{ type: 'productType' | 'category' | 'tag'; handle: string; label: string }> = [];
    
    if (value.productType) {
      chips.push({ type: 'productType', handle: value.productType, label: findLabelByHandle(value.productType) });
    }
    if (value.category) {
      chips.push({ type: 'category', handle: value.category, label: findLabelByHandle(value.category) });
    }
    value.tags.forEach(tagHandle => {
      chips.push({ type: 'tag', handle: tagHandle, label: findLabelByHandle(tagHandle) });
    });
    
    return chips;
  }, [value]);

  const handleRemoveChip = (chip: { type: 'productType' | 'category' | 'tag'; handle: string }) => {
    const newState: TaxonomyFilterState = { ...value };
    
    switch (chip.type) {
      case 'productType':
        newState.productType = undefined;
        newState.category = undefined;
        newState.tags = [];
        break;
      case 'category':
        newState.category = undefined;
        newState.tags = [];
        break;
      case 'tag':
        newState.tags = value.tags.filter(t => t !== chip.handle);
        break;
    }
    
    onChange(newState);
    onPageReset?.();
  };

  const handleToggleExpand = (nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const handleSelect = (node: TaxonomyNode) => {
    const newState: TaxonomyFilterState = { ...value };
    
    // Check if this node is already selected - if so, deselect (toggle behavior)
    const isAlreadySelected = 
      (node.type === 'collection' && value.collection === node.handle) ||
      (node.type === 'productType' && value.productType === node.handle) ||
      (node.type === 'category' && value.category === node.handle);
    
    if (isAlreadySelected) {
      // Deselect - clear this level and deeper
      switch (node.type) {
        case 'collection':
          newState.collection = undefined;
          newState.productType = undefined;
          newState.category = undefined;
          newState.tags = [];
          break;
        case 'productType':
          newState.productType = undefined;
          newState.category = undefined;
          newState.tags = [];
          break;
        case 'category':
          newState.category = undefined;
          newState.tags = [];
          break;
      }
      // Collapse the node
      setExpandedNodes(prev => {
        const next = new Set(prev);
        next.delete(node.id);
        return next;
      });
    } else {
      // Select - set this level and clear deeper
      // Accordion behavior: collapse all other nodes at same level, expand only selected
      const collapseAllExcept = (keepNodeId: string) => {
        setExpandedNodes(prev => {
          const next = new Set<string>();
          // Only keep the selected node expanded
          next.add(keepNodeId);
          return next;
        });
      };
      
      switch (node.type) {
        case 'collection':
          newState.collection = node.handle;
          newState.productType = undefined;
          newState.category = undefined;
          newState.tags = [];
          collapseAllExcept(node.id);
          break;
          
        case 'productType':
          newState.productType = node.handle;
          newState.category = undefined;
          newState.tags = [];
          if (node.collectionHandle) {
            newState.collection = node.collectionHandle;
          }
          collapseAllExcept(node.id);
          break;
          
        case 'category':
          newState.category = node.handle;
          newState.tags = [];
          // Find parent productType - need to search recursively since rootNodes may be collections
          let parentPT: TaxonomyNode | undefined;
          let parentCollection: TaxonomyNode | undefined;
          
          // Search for the productType that contains this category
          const findParentPT = (nodes: TaxonomyNode[], parent?: TaxonomyNode): TaxonomyNode | undefined => {
            for (const n of nodes) {
              if (n.type === 'productType' && n.children?.some(c => c.id === node.id)) {
                return n;
              }
              if (n.children) {
                const found = findParentPT(n.children, n);
                if (found) return found;
              }
            }
            return undefined;
          };
          
          parentPT = findParentPT(rootNodes);
          
          // Also find the collection if rootNodes are collections
          if (rootNodes[0]?.type === 'collection') {
            parentCollection = rootNodes.find(c => 
              c.children?.some(pt => pt.id === parentPT?.id) ||
              c.children?.some(pt => pt.children?.some(cat => cat.id === node.id))
            );
          }
          
          if (parentPT) {
            newState.productType = parentPT.handle;
            if (parentPT.collectionHandle) {
              newState.collection = parentPT.collectionHandle;
            } else if (parentCollection) {
              newState.collection = parentCollection.handle;
            }
          }
          
          // Keep parent collection, productType AND category expanded
          setExpandedNodes(() => {
            const next = new Set<string>();
            if (parentCollection) {
              next.add(parentCollection.id);
            }
            if (parentPT) {
              next.add(parentPT.id);
            }
            next.add(node.id);
            return next;
          });
          break;
      }
    }
    
    onChange(newState);
    onPageReset?.();
  };

  const handleTagToggle = (tagHandle: string, checked: boolean) => {
    const newState: TaxonomyFilterState = { ...value };
    
    if (checked) {
      // Find if this tag has a parent tag in the taxonomy (i.e., it's a nested tag)
      const findParentTag = (nodes: TaxonomyNode[]): string | undefined => {
        for (const node of nodes) {
          if (node.type === 'tag' && node.children) {
            // Check if any child matches this handle
            const isChild = node.children.some(child => child.handle === tagHandle);
            if (isChild) {
              return node.handle; // Return parent tag handle
            }
          }
          if (node.children) {
            const found = findParentTag(node.children);
            if (found) return found;
          }
        }
        return undefined;
      };
      
      const parentTagHandle = findParentTag(CLOTHING_FILTER_NODES);
      
      // Remove parent tag if present (selecting a sub-tag should replace parent)
      if (parentTagHandle && value.tags.includes(parentTagHandle)) {
        newState.tags = value.tags.filter(t => t !== parentTagHandle);
      }
      
      newState.tags = [...newState.tags, tagHandle];
    } else {
      newState.tags = value.tags.filter(t => t !== tagHandle);
    }
    
    onChange(newState);
    onPageReset?.();
  };

  const handleClearAll = () => {
    onChange({
      collection: undefined,
      productType: undefined,
      category: undefined,
      tags: [],
    });
    // Collapse all menus
    setExpandedNodes(new Set());
    onPageReset?.();
  };

  const hasActiveFilters = value.collection || value.productType || value.category || value.tags.length > 0;

  return (
    <div className={cn("space-y-2", className)}>
      {/* Header with clear button */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">Categories</span>
        {hasActiveFilters && (
          <button
            onClick={handleClearAll}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Clear All
          </button>
        )}
      </div>
      
      {/* "All" option */}
      <label className="flex items-center gap-2 py-1 cursor-pointer hover:text-foreground transition-colors">
        <input
          type="radio"
          name="taxonomy-all"
          checked={!hasActiveFilters}
          onChange={handleClearAll}
          className="w-3.5 h-3.5 accent-primary cursor-pointer"
        />
        <span className={cn(
          "text-sm flex-1",
          !hasActiveFilters ? "text-foreground font-medium" : "text-muted-foreground"
        )}>
          {allLabel}
        </span>
        {totalCount !== undefined && totalCount > 0 && (
          <span className="text-xs text-muted-foreground/70">({totalCount})</span>
        )}
      </label>
      
      {/* Taxonomy tree */}
      <div className="space-y-0.5">
        {rootNodes.map(node => (
          <TaxonomyNodeItem
            key={node.id}
            node={node}
            depth={0}
            filterState={value}
            onSelect={handleSelect}
            onTagToggle={handleTagToggle}
            expandedNodes={expandedNodes}
            onToggleExpand={handleToggleExpand}
            counts={counts}
          />
        ))}
      </div>

      {/* Active filter chips - at bottom so tree doesn't jump */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border/50">
          {activeChips.map(chip => (
            <button
              key={`${chip.type}-${chip.handle}`}
              onClick={() => handleRemoveChip(chip)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors group"
            >
              <span>{chip.label}</span>
              <X className="w-3 h-3 opacity-50 group-hover:opacity-100" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TaxonomyFilter;
